import debounce from 'debounce';

const pluginName = 'subEventValidationPlugin';
const defaults = {
  eventId: null,
  registrantUuid: null,
  registrantPersonalInformation: null,
  locale: null,
  subEventName: null,
  routes: {
    validation: null,
  },
  callbacks: {
    initialized: () => {},
    setup: () => {},
    getGivenName: () => '',
    getFamilyName: () => '',
    getDateOfBirth: () => '',
    getSubEvent: () => '',
  },
};
const selectors = {
  snackBar: '[data-js-snack-bar]',
  form: '.js-registrant-sub-event-validation-form',
  pencilEditIcon: '[data-label-id]',
  identifiers: '[data-js-sub-event-validation-identifiers]',
  personalInformationFamilyName: '[data-js-sub-event-validation-personal-information-family-name]',
  personalInformationGivenName: '[data-js-sub-event-validation-personal-information-given-name]',
  personalInformationDateOfBirth: '[data-js-sub-event-validation-personal-information-date-of-birth]',
  identifier: '[data-js-sub-event-validation-identifier]',
  feedbackContainer: '[data-js-sub-event-validation-feedback-container]',
  feedbackPending: '[data-js-sub-event-validation-feedback-pending]',
  feedbackSuccess: '[data-js-sub-event-validation-feedback-success]',
  feedbackError: '[data-js-sub-event-validation-feedback-error]',
  feedbackErrorText: '[data-js-sub-event-validation-feedback-error-text]',
  submit: '[data-js-sub-event-validation-submit]',
};
const classes = {
  hidden: 'd-none hidden',
  incomplete: 'font-style-italic text-muted',
};

/**
 * Get the elements
 * @param data
 * @returns {{}}
 */
const getElements = (data) => {
  const $feedbackContainer = data.elements.$element.find(selectors.feedbackContainer);
  const $feedbackErrorText = $feedbackContainer.find(selectors.feedbackErrorText);

  return {
    $snackBar: $(selectors.snackBar),
    $identifiers: data.elements.$element.find(selectors.identifiers),
    $personalInformationFamilyName: data.elements.$element.find(selectors.personalInformationFamilyName),
    $personalInformationGivenName: data.elements.$element.find(selectors.personalInformationGivenName),
    $personalInformationDateOfBirth: data.elements.$element.find(selectors.personalInformationDateOfBirth),
    $identifier: data.elements.$element.find(selectors.identifier),
    $feedbackContainer,
    $feedbackPending: $feedbackContainer.find(selectors.feedbackPending),
    $feedbackSuccess: $feedbackContainer.find(selectors.feedbackSuccess),
    $feedbackError: $feedbackContainer.find(selectors.feedbackError),
    $feedbackErrorText,
    $feedbackErrorTextOriginal: $feedbackErrorText.clone(),
    $feedbackErrorTextPencilEditIcon: $feedbackErrorText.find(selectors.pencilEditIcon).clone(),
    $submit: data.elements.$element.find(selectors.submit),
  };
};

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

/**
 * Toggle the visibility of the plugin
 * @param data
 * @param isVisible
 */
const toggleVisibility = (data, isVisible) => {
  data.elements.$element.toggleClass(classes.hidden, !isVisible);
};

/**
 * Reset the sub-event validation state
 * @param data
 */
const resetFeedback = (data) => {
  data.elements.$feedbackContainer.addClass(classes.hidden);
  data.elements.$feedbackPending.addClass(classes.hidden);
  data.elements.$feedbackSuccess.addClass(classes.hidden);
  data.elements.$feedbackError.addClass(classes.hidden);
  data.elements.$element.preventDoubleSubmission('reEnable');
};

/**
 * Reset the plugin
 * @param {*} data
 */
const reset = (data) => {
  resetFeedback(data);
};

/**
 * Get the sub-event name
 * @param data
 * @returns {string}
 */
const getSubEventName = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getSubEvent();
  }

  return data.settings.subEventName;
};

/**
 * Get the family name
 * @param data
 * @returns {string}
 */
const getFamilyName = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getFamilyName();
  }

  return data.settings.registrantPersonalInformation.person.familyName;
};

/**
 * Get the given name
 * @param data
 * @returns {string}
 */
const getGivenName = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getGivenName();
  }

  return data.settings.registrantPersonalInformation.person.givenName;
};

/**
 * Get the date of birth
 * @param data
 * @returns {string}
 */
const getDateOfBirth = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getDateOfBirth();
  }

  return data.settings.registrantPersonalInformation.person.dateOfBirth;
};

/**
 * Get the fields
 * @param data
 * @returns {array}
 */
const getFields = (data) => {
  const fields = [];

  if (data.elements.$personalInformationFamilyName.length) {
    fields.push({
      field: 'family_name',
      value: getFamilyName(data),
    });
  }

  if (data.elements.$personalInformationGivenName.length) {
    fields.push({
      field: 'given_name',
      value: getGivenName(data),
    });
  }

  if (data.elements.$personalInformationDateOfBirth.length) {
    fields.push({
      field: 'dob',
      value: getDateOfBirth(data),
    });
  }

  if (data.elements.$identifier.length) {
    fields.push({
      field: 'identifier',
      value: data.elements.$identifier.val(),
    });
  }

  return fields;
};

/**
 * Get the list identifiers
 * @param data
 * @returns {array}
 */
const getIdentifiers = (data) => data.elements.$identifiers.val().split(',');

/**
 * Set the feedback error state
 * @param data
 */
const setFeedbackErrorState = (data) => {
  const subEventName = getSubEventName(data);
  const errorText = data.elements.$feedbackErrorTextOriginal
    .text()
    .replace('%subEvent%', subEventName);

  data.elements.$feedbackContainer.removeClass(classes.hidden);
  data.elements.$feedbackError.removeClass(classes.hidden);
  data.elements.$feedbackErrorText.replaceWith(
    data.elements.$feedbackErrorTextOriginal
      .clone()
      .text(errorText)
      .append(data.elements.$feedbackErrorTextPencilEditIcon),
  );
};

/**
 * Validate discount code
 * @param data
 */
const validate = debounce((data) => {
  resetFeedback(data);

  data.elements.$feedbackContainer.removeClass(classes.hidden);
  data.elements.$feedbackPending.removeClass(classes.hidden);

  data.requests.validate = $.ajax({
    url: data.settings.routes.validation,
    type: 'POST',
    data: {
      validator_type: 'validation-list',
      identifiers: getIdentifiers(data),
      fields: getFields(data),
    },
  })
    .always(() => {
      data.requests.validate = null;
      data.elements.$feedbackPending.addClass(classes.hidden);
    })
    .done((response) => {
      if (response.valid) {
        data.elements.$feedbackSuccess.removeClass(classes.hidden);
        return;
      }

      setFeedbackErrorState(data);
    })
    .fail(() => {
      data.elements.$snackBar.snackBar('add', {
        type: 'danger',
        content: data.settings.i18n.genericError,
      });
    });
}, 500);

/**
 * Check if the plugin can validate
 * @param data
 * @returns {boolean}
 */
const canValidate = (data) => {
  if (data.elements.$identifier.length && !data.elements.$identifier.val()) {
    return false;
  }

  if (data.elements.$personalInformationFamilyName.length && !getFamilyName(data)) {
    return false;
  }

  if (data.elements.$personalInformationGivenName.length && !getGivenName(data)) {
    return false;
  }

  if (data.elements.$personalInformationDateOfBirth.length && !getDateOfBirth(data)) {
    return false;
  }

  return true;
};

/**
 * Check if the plugin can automatically validate
 * @param data
 * @returns {boolean}
 */
const canAutomaticallyValidate = (data) => {
  if (!canValidate(data)) {
    resetFeedback(data);
    return false;
  }

  // if the identifier field (e.g. membership id) is present, we stop auto-validation
  if (data.elements.$identifier.length) {
    return false;
  }

  return true;
};

/**
 * Update the sub-event validation
 * @param data
 */
const updatePersonalInformationValues = (data) => {
  if (data.elements.$personalInformationFamilyName.length) {
    const familyName = getFamilyName(data);

    if (familyName) {
      data.elements.$personalInformationFamilyName.removeClass(classes.incomplete);
      data.elements.$personalInformationFamilyName.text(familyName);
    } else {
      data.elements.$personalInformationFamilyName.addClass(classes.incomplete);
      data.elements.$personalInformationFamilyName.text(data.settings.i18n.incomplete);
    }
  }

  if (data.elements.$personalInformationGivenName.length) {
    const givenName = getGivenName(data);

    if (givenName) {
      data.elements.$personalInformationGivenName.removeClass(classes.incomplete);
      data.elements.$personalInformationGivenName.text(givenName);
    } else {
      data.elements.$personalInformationGivenName.addClass(classes.incomplete);
      data.elements.$personalInformationGivenName.text(data.settings.i18n.incomplete);
    }
  }

  if (data.elements.$personalInformationDateOfBirth.length) {
    const dateOfBirth = getDateOfBirth(data);

    if (dateOfBirth) {
      data.elements.$personalInformationDateOfBirth.removeClass(classes.incomplete);
      data.elements.$personalInformationDateOfBirth.text(dateOfBirth);
    } else {
      data.elements.$personalInformationDateOfBirth.addClass(classes.incomplete);
      data.elements.$personalInformationDateOfBirth.text(data.settings.i18n.incomplete);
    }
  }

  if (canAutomaticallyValidate(data)) {
    validate(data);
  }
};

/**
 * Serialize the start group selection
 * @param data
 * @returns {string}
 */
const serialize = (data) => {
  const formData = data.elements.$element.serializeArray();

  const fields = getFields(data);

  fields.forEach((field, index) => {
    formData.push({ name: `sub-event-validation[fields][${index}][field]`, value: field.field });
    formData.push({ name: `sub-event-validation[fields][${index}][value]`, value: field.value });
  });

  formData.push({ name: 'sub-event-validation[validator_type]', value: 'validation-list' });

  const identifiers = getIdentifiers(data);

  identifiers.forEach((identifier, index) => {
    formData.push({ name: `sub-event-validation[identifiers][${index}]`, value: identifier });
  });

  return $.param(formData);
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = (data) => {
  if (data.requests.validate) {
    data.requests.validate.abort();
    data.requests.validate = null;
  }
};

/**
 * Clear the plugin's contents
 */
const clear = (data) => {
  cancelActiveRequests(data);
  data.elements.$element.empty();
};

/**
 * Setup the plugin
 * @param data
 * @param html
 */
const setup = (data, html) => {
  data.elements.$element.html(html);

  $.extend(data.elements, getElements(data));

  reset(data);

  data.elements.$element
    .preventDoubleSubmission()
    .on('submit', (event) => {
      if (!data.elements.$element[0].checkValidity()) {
        return;
      }

      event.preventDefault();

      if (canValidate(data)) {
        validate(data);
      } else {
        data.elements.$element.preventDoubleSubmission('reEnable');
        setFeedbackErrorState(data);
      }
    });

  data.state.setup = true;

  updatePersonalInformationValues(data);

  triggerHook(data, 'setup');
};

const methods = {
  toggleVisibility,
  setup,
  initializeContent: setup,
  shouldUsePersonalInformationCallbacks: (data) => {
    data.state.shouldUsePersonalInformationCallbacks = true;
  },
  update: (data) => {
    if (!data.state.setup) {
      return;
    }

    updatePersonalInformationValues(data);
  },
  clear,
  cancelActiveRequests,
};

const getMethods = {
  serialize: (data) => {
    if (!data.state.setup) {
      return '';
    }

    return serialize(data);
  },
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const elements = {
    $element,
  };
  const requests = {
    validate: null,
  };
  const data = {
    settings,
    elements,
    requests,
    state: {
      setup: false,
      shouldUsePersonalInformationCallbacks: false,
    },
  };

  data.elements.$element.data(pluginName, data);
  triggerHook(data, 'initialized');
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

plugin.methods = methods;
plugin.getMethods = getMethods;

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
